/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/contexts/WorkspaceContext.tsx
 * DESCRIPTION: Global context holding the current workspace scope.
 *              All data-fetching hooks and components read from here
 *              to determine which org's data to display.
 * 
 * SCOPES:
 *   'global'       → Admin-only platform-wide view
 *   'personal'     → User's 1-person workspace
 *   'organization' → Team org (multi-member)
 * 
 * USAGE:
 *   const { scope, activeOrgId, switchScope } = useWorkspace();
 * 
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-09-04
 * ─────────────────────────────────────────────────────────────────────
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

export type WorkspaceScope = 'global' | 'personal' | 'organization';

interface WorkspaceContextValue {
  /** Current viewing scope */
  scope: WorkspaceScope;
  /** Active organization ID (null for global scope) */
  activeOrgId: string | null;
  /** Active organization name (for display) */
  activeOrgName: string | null;
  /** Switch to a different scope */
  switchScope: (scope: WorkspaceScope, orgId?: string, orgName?: string) => void;
  /** Whether the workspace is still initializing */
  isInitializing: boolean;
  /** Mark initialization complete */
  setInitialized: (ready: boolean) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [scope, setScope] = useState<WorkspaceScope>('personal');
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [activeOrgName, setActiveOrgName] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const switchScope = useCallback((
    newScope: WorkspaceScope,
    orgId?: string,
    orgName?: string
  ) => {
    setScope(newScope);
    setActiveOrgId(orgId || null);
    setActiveOrgName(orgName || null);
  }, []);

  const setInitialized = useCallback((ready: boolean) => {
    setIsInitializing(!ready);
  }, []);

  return (
    <WorkspaceContext.Provider value={{
      scope,
      activeOrgId,
      activeOrgName,
      switchScope,
      isInitializing,
      setInitialized,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

/**
 * Hook to access the current workspace context.
 * Must be used within a <WorkspaceProvider>.
 */
export const useWorkspace = (): WorkspaceContextValue => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};
